import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PdpPhase, TaskStatus } from '../../lib/constants';

/** Client-only UI state: the leadership dashboard filters. */
interface FiltersState {
  /**
   * Selected program (project) ids. `null` means "all programs" (the default,
   * no filter); an array is an explicit selection, and an empty array means
   * "none" — distinct from "all" so unchecking the last program shows nothing
   * rather than silently reverting to everything.
   */
  programs: string[] | null;
  /** Selected PDP phases. Empty array means "all phases". */
  phases: PdpPhase[];
  status: TaskStatus | 'all';
}

const initialState: FiltersState = {
  programs: null,
  phases: [],
  status: 'all',
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    /** Set the program selection; pass `null` to mean "all programs". */
    setPrograms(state, action: PayloadAction<string[] | null>) {
      state.programs = action.payload;
    },
    togglePhase(state, action: PayloadAction<PdpPhase>) {
      const phase = action.payload;
      state.phases = state.phases.includes(phase)
        ? state.phases.filter((p) => p !== phase)
        : [...state.phases, phase];
    },
    clearPhases(state) {
      state.phases = [];
    },
    setStatus(state, action: PayloadAction<TaskStatus | 'all'>) {
      state.status = action.payload;
    },
    resetFilters() {
      return initialState;
    },
  },
});

export const {
  setPrograms,
  togglePhase,
  clearPhases,
  setStatus,
  resetFilters,
} = filtersSlice.actions;
export default filtersSlice.reducer;
